import type { AssignableRole } from "@unbox/api";
import { Plus, SearchLg, Users01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useMe } from "@/api/me";
import {
  useAddWorkspaceTeamMember,
  useRemoveWorkspaceTeamMember,
  useResendWorkspaceTeamInvite,
  useRevokeWorkspaceTeamInvite,
  useUpdateWorkspaceTeamInvite,
  useUpdateWorkspaceTeamMember,
  useWorkspaceTeam,
} from "@/api/workspace-team";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TeamBrandsCell } from "@/components/team/team-brands-cell";
import { TeamConfirmModal } from "@/components/team/team-confirm-modal";
import { type TeamInviteRow, type TeamRow, TeamTable } from "@/components/team/team-table";
import { type WorkspaceMemberFormValues, WorkspaceMemberModal } from "@/components/team/workspace-member-modal";
import { usePermissions } from "@/hooks/use-permissions";
import { useWorkspaceAdmin } from "@/hooks/use-workspace-admin";
import { success } from "@/providers/ToastProvider";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";

type WorkspaceGrantsRow = TeamRow & { grants: Omit<WorkspaceMemberFormValues, "email">; brandIds: string[] };

export function WorkspaceTeamPage() {
  const { data: me } = useMe();
  const { workspace } = useWorkspaceAdmin();
  const { invitableRoles } = usePermissions();
  const { data, isLoading } = useWorkspaceTeam();

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<WorkspaceGrantsRow | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [removing, setRemoving] = useState<WorkspaceGrantsRow | null>(null);

  const addMember = useAddWorkspaceTeamMember();
  const updateMember = useUpdateWorkspaceTeamMember();
  const updateInvite = useUpdateWorkspaceTeamInvite();
  const removeMember = useRemoveWorkspaceTeamMember();
  const resendInvite = useResendWorkspaceTeamInvite();
  const revokeInvite = useRevokeWorkspaceTeamInvite();

  const brands = useMemo(
    () => (workspace?.merchants ?? []).map(({ id, name, logoUrl }) => ({ id, name, logoUrl })),
    [workspace?.merchants],
  );
  const brandsById = useMemo(() => new Map(brands.map((brand) => [brand.id, brand])), [brands]);

  const rows: WorkspaceGrantsRow[] = useMemo(() => {
    if (!data) return [];

    // A Workspace Admin holds no per-brand grants; their reach is every brand.
    const reachOf = (grants: Omit<WorkspaceMemberFormValues, "email">) =>
      grants.workspaceAdmin ? brands.map((brand) => brand.id) : grants.brands.map((brand) => brand.merchantId);

    const rolesOf = (grants: Omit<WorkspaceMemberFormValues, "email">): AssignableRole[] =>
      grants.workspaceAdmin ? ["workspace_admin"] : grants.brands.map((brand) => brand.role);

    const invites: WorkspaceGrantsRow[] = data.invites.map((invite) => {
      const grants = { workspaceAdmin: invite.isWorkspaceAdmin, brands: invite.brands };
      return {
        kind: "invite",
        id: `invite-${invite.id}`,
        inviteId: invite.id,
        email: invite.email,
        roles: rolesOf(grants),
        createdAt: new Date(invite.createdAt),
        expired: new Date(invite.expiresAt) <= new Date(),
        grants,
        brandIds: reachOf(grants),
      };
    });

    const members: WorkspaceGrantsRow[] = data.members.map((member) => {
      const grants = { workspaceAdmin: member.isWorkspaceAdmin, brands: member.brands };
      return {
        kind: "member" as const,
        id: `member-${member.id}`,
        userId: member.id,
        email: member.email,
        name: `${member.firstName} ${member.lastName}`.trim(),
        roles: rolesOf(grants),
        lastActivity: member.lastActivity ? new Date(member.lastActivity) : null,
        createdAt: new Date(member.createdAt),
        // Only a brand page has rows it cannot edit; this one owns the grant.
        managedAtWorkspaceLevel: false,
        isSelf: member.id === me?.id,
        grants,
        brandIds: reachOf(grants),
      };
    });

    return [...invites, ...members];
  }, [data, me?.id, brands]);

  const rowsById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (row) =>
        row.email.toLowerCase().includes(term) || (row.kind === "member" && row.name.toLowerCase().includes(term)),
    );
  }, [rows, search]);

  const submitMember = ({ email, ...grants }: WorkspaceMemberFormValues) => {
    if (!editing) {
      addMember.mutate(
        { email, ...grants },
        {
          onSuccess: (result) => {
            success(result.type === "invite" ? "Invite sent" : "Added to the team");
            setIsAdding(false);
          },
        },
      );
      return;
    }

    const onSuccess = () => {
      success("Access updated");
      setEditing(null);
    };

    if (editing.kind === "member") {
      updateMember.mutate({ userId: editing.userId, grants }, { onSuccess });
    } else {
      updateInvite.mutate({ inviteId: editing.inviteId, grants }, { onSuccess });
    }
  };

  const onResend = (row: TeamInviteRow) => {
    resendInvite.mutate(row.inviteId, { onSuccess: () => success("Invite sent") });
  };

  const confirmRemove = () => {
    if (!removing) return;

    if (removing.kind === "invite") {
      revokeInvite.mutate(removing.inviteId, {
        onSuccess: () => {
          success("Invite revoked");
          setRemoving(null);
        },
      });
    } else {
      removeMember.mutate(removing.userId, {
        onSuccess: () => {
          success("Access removed");
          setRemoving(null);
        },
      });
    }
  };

  return (
    <div className="flex flex-col w-full h-screen gap-8 items-center overflow-y-auto bg-unbox-cream-25">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col gap-8 px-6 lg:px-16 pt-16 pb-8`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-normal text-secondary flex flex-row items-center gap-2 mb-1">
              <Users01 className="w-5 h-5" strokeWidth={1.8} />
              Team
            </h1>
            <span className="text-quaternary text-sm">Manage all team members with access</span>
          </div>

          <div className="flex items-center gap-3">
            <Input
              aria-label="Search Team"
              placeholder="Search Team"
              icon={SearchLg}
              value={search}
              onChange={setSearch}
              wrapperClassName="w-[280px]"
            />
            <Button color="primary" size="md" iconLeading={Plus} onClick={() => setIsAdding(true)}>
              team member
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-primary bg-primary overflow-hidden">
          {isLoading ? (
            <p className="text-sm text-tertiary p-6">Loading team...</p>
          ) : (
            <TeamTable
              rows={visibleRows}
              assignableRoles={invitableRoles}
              onEdit={(row) => setEditing(rowsById.get(row.id) ?? null)}
              onRemove={(row) => setRemoving(rowsById.get(row.id) ?? null)}
              onResend={onResend}
              extraColumn={{
                id: "brands",
                label: "Merchants",
                render: (row) => (
                  <TeamBrandsCell
                    brands={(rowsById.get(row.id)?.brandIds ?? []).flatMap((id) => {
                      const brand = brandsById.get(id);
                      return brand ? [brand] : [];
                    })}
                  />
                ),
              }}
              emptyMessage={
                search.trim() ? "No team members match your search." : "Team members with access will appear here."
              }
            />
          )}
        </div>
      </div>

      <WorkspaceMemberModal
        isOpen={isAdding || !!editing}
        onOpenChange={(open) => {
          if (open) return;
          setIsAdding(false);
          setEditing(null);
        }}
        workspace={{ name: workspace?.name ?? "this workspace", logoUrl: workspace?.logoUrl, brands }}
        assignableRoles={invitableRoles}
        editing={editing ? { email: editing.email, ...editing.grants } : null}
        onSubmit={submitMember}
        isPending={addMember.isPending || updateMember.isPending || updateInvite.isPending}
      />

      <TeamConfirmModal
        isOpen={!!removing}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={removing?.kind === "invite" ? "Revoke this invite?" : "Remove access?"}
        description={
          removing?.kind === "invite"
            ? `${removing.email} will no longer be able to use their invite link.`
            : `${removing?.email} will immediately lose access to every brand in ${workspace?.name ?? "this workspace"}.`
        }
        confirmLabel={removing?.kind === "invite" ? "Revoke invite" : "Remove access"}
        onConfirm={confirmRemove}
        isPending={removeMember.isPending || revokeInvite.isPending}
      />
    </div>
  );
}
